import { Component, ContentChild, ContentChildren, ElementRef, EventEmitter, Input, NgZone, OnInit, Optional, Output, Self, TemplateRef, ViewChild, forwardRef } from '@angular/core';
import { InfiniteScrollCustomEvent, IonModal, IonSearchbar } from '@ionic/angular/standalone';
import { BehaviorSubject, Observable, Subject, firstValueFrom, from, takeUntil } from 'rxjs';
import { IonicComponentsModule } from 'src/app/modules/ionic-components.module';
import InfiniteScrollSubject from 'src/app/subjects/infiniteScrollSubject';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { UtilService } from 'src/app/services/util.service';

@Component({
  selector: 'fv-combobox-with-store',
  templateUrl: './combobox-with-store.component.html',
  styleUrls: ['./combobox-with-store.component.scss'],
  standalone: true,
  imports: [
    IonicComponentsModule
  ]
})
export class ComboboxWithStoreComponent implements ControlValueAccessor {

  //#region Propiedades

  @Input("label") public label: string
  @Input("value-field") public valueField: string = 'Id'
  @Input("display-field") public displayField: string = 'Text'
  @Input("placeholder") public placeholder: string = "[Selecciona]"
  @Input("modal-title") public modalTitle: string = "Elije una opción"
  @Input("accept-text") public acceptText: string = "Aceptar"
  @Input("cancel-text") public cancelText: string = "Cancelar"
  @ViewChild("modal") public modal: IonModal
  @ContentChild("itemTpl") public comboItemTpl: TemplateRef<any>
  @ContentChild("displayTpl") public displayTpl: TemplateRef<any>
  @ContentChild("selectorTpl") public selectorTpl: TemplateRef<any>
  @Input("data-source") public dataSource: InfiniteScrollSubject<any>
  @Input("page-size") public pageSize: number = 5
  @Input("show-search") public showSearch: boolean = false
  @Input("search-placeholder") public searchPlaceholder: string = 'Buscar...'
  @Input("empty-text") public emptyText: string = "No se han encontrado elementos"
  @ViewChild("searchBar") public searchBar: IonSearchbar
  @ViewChild("refresher", { read: ElementRef<HTMLElement> }) public refresher: ElementRef<HTMLElement>
  @Input("readonly") public readonly: boolean
  @Input("obligatorio") public obligatorio: boolean // Esto es solo para pintar un asterisco, y este campo no se lee de los validadores, sino que hay que establecerlo a mano.
  @Input("allow-blank") public allowBlank: boolean = true 

  private cancelSetValue$ = new Subject<void>()
  public text = new BehaviorSubject<string>('')
  public item = new BehaviorSubject<any>(null)
  private _value: any
  public get value(): any {
    return this._value
  }
  @Input("value") public set value(val: any) {
    if (this._value !== val) {
      console.log(`Cambia value del combo: ${val}`)
      this._value = val
      let text = this.text
      let item = this.item
      if (this.dataSource != null && this.dataSource.getById != null) {
        if (this._value == null) {
          console.log(`Texto: ${null}`)
          text.next(null)
          item.next(null)
          this.cancelSetValue$.next()
        } else {
          from(this.dataSource.getById(this._value)).pipe(
            takeUntil(this.cancelSetValue$)
          ).subscribe((itemById) => {
            if (itemById != null && itemById[this.displayField] != null) {
              console.log(`Texto: ${itemById[this.displayField]}`)
              text.next(itemById[this.displayField])
              item.next(itemById)
            } else {
              console.log(`Texto: ${null}`)
              text.next(null)
              item.next(null)
            }
            this.cancelSetValue$.next()
          })
        }
      }
    }
  }
  @Output("valueChange") public valueChange = new EventEmitter<any>()
  @Output("valueSelected") public valueSelected = new EventEmitter<{newValue: string, oldValue: string}>()

  //#endregion

  //#region Constructor

  constructor(
    private util: UtilService,
    @Self() @Optional() private control: NgControl
  ) { 
    if (this.control != null) this.control.valueAccessor = this
  }

  //#endregion

  //#region Loader

  public showLoader(): void {
    this.refresher.nativeElement.classList.add('refresher-active')
    this.refresher.nativeElement.classList.add('refresher-refreshing')
  }

  public hideLoader(): void {
    this.refresher.nativeElement.classList.remove('refresher-active')
    this.refresher.nativeElement.classList.remove('refresher-refreshing')
  }

  //#endregion

  //#region Open

  public async open(): Promise<void> {
    if (!this.readonly) {
      this.modal.isOpen = true
      await this.util.Sleep(0)
      if (this.searchBar != null) {
        this.searchBar.value = ''
      }
      if (this.dataSource != null) {
        this.dataSource.search = ''
        await this.dataSource.reset()
      }
    }
  }

  //#endregion

  //#region Eventos

  public optionSelected(option: any) {
    let oldValue = this._value?.toString()
    if (option == null || option[this.valueField] == null) {
      this.value = null
    } else {
      this.value = option[this.valueField]
    }
    this.valueChange.emit(this.value)
    this.valueSelected.emit({newValue: this.value, oldValue: oldValue})
    this.onChange(this.value)
    this.modal.isOpen = false
    this.onTouched()
  }

  public async onSearch(): Promise<void> {
    if (this.dataSource != null) {
      try {
        this.dataSource.search = this.searchBar.value
        this.showLoader()
        await this.dataSource.reset()
      } catch (ex) { }
      this.hideLoader()
    }
  }

  public async onIonInfinite(ev: InfiniteScrollCustomEvent): Promise<void> {
    if (await firstValueFrom(this.dataSource.HasMoreItems)) {
      await this.dataSource.loadPage()
    }
    ev.target.complete()
  }

  public async handleRefresh(ev: Event): Promise<void> {
    if (this.dataSource != null) {
      await this.dataSource.reset()
      let target = ev.target as HTMLIonRefresherElement
      target.complete()
    }
  }

  //#endregion

  //#region Reactive Forms

  private onChange = (value: any) => { }
  private onTouched = () => { }

  writeValue(value: any): void {
    this.value = value
    this.valueChange.emit(value)
    this.onChange(value)
  }

  registerOnChange(fn: (value: any) => {}): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn
  }

  public isInvalid(): boolean {
    return this.control ? this.control.invalid : false
  }

  public isTouched(): boolean {
    return this.control ? this.control.touched : false
  }

  //#endregion

}
